import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Send, Clear, Delete, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../../contexts/DeviceContext';
import {
  talkingPalService,
  TalkingPalData,
  SESSION_LIMIT_MINUTES_MIN,
  SESSION_LIMIT_MINUTES_MAX,
  SESSION_LIMIT_MINUTES_DEFAULT,
} from '../../services/talkingPalService';

const TalkingpalPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDevice, showSnackbar } = useDevice();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingPrompt, setSendingPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [systemPrompt, setSystemPrompt] = useState('');
  const [switchEnabled, setSwitchEnabled] = useState(false);
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [promptHistory, setPromptHistory] = useState<string[]>([]);
  const [currentPrompt, setCurrentPrompt] = useState<string | null>(null);
  const [sessionLimit, setSessionLimit] = useState(SESSION_LIMIT_MINUTES_DEFAULT);
  const [sessionLimitInput, setSessionLimitInput] = useState(String(SESSION_LIMIT_MINUTES_DEFAULT));
  const [savingSessionLimit, setSavingSessionLimit] = useState(false);

  // Nursery rhymes state
  const [nurseryRhymes, setNurseryRhymes] = useState<string[]>([]);
  const [rhymeText, setRhymeText] = useState('');
  const [editingRhymeIndex, setEditingRhymeIndex] = useState<number | null>(null);
  const [savingRhyme, setSavingRhyme] = useState(false);

  // Track if form has unsaved changes (only for system prompt now)
  const [originalSystemPrompt, setOriginalSystemPrompt] = useState('');

  const hasUnsavedChanges = systemPrompt !== originalSystemPrompt;

  // Load TalkingPal data
  const loadData = useCallback(async () => {
    if (!selectedDevice) return;

    setLoading(true);
    setError(null);

    try {
      const data: TalkingPalData = await talkingPalService.getTalkingPal(selectedDevice.id);
      setSystemPrompt(data.system_prompt || '');
      setOriginalSystemPrompt(data.system_prompt || '');
      setSwitchEnabled(data.switch);
      setPromptHistory(data.prompt_history || []);
      setCurrentPrompt(data.current_prompt);
      setNurseryRhymes(data.nursery_rhymes || []);
      const limit = data.session_limit_minutes ?? SESSION_LIMIT_MINUTES_DEFAULT;
      setSessionLimit(limit);
      setSessionLimitInput(String(limit));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load TalkingPal settings');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save system prompt
  const handleSave = async () => {
    if (!selectedDevice) return;

    setSaving(true);
    try {
      const data = await talkingPalService.updateTalkingPal(selectedDevice.id, {
        system_prompt: systemPrompt,
      });
      setOriginalSystemPrompt(data.system_prompt || '');
      showSnackbar(t('talkingpal.saved'), 'success');
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Toggle switch immediately
  const handleSwitchToggle = async (checked: boolean) => {
    if (!selectedDevice) return;

    // Optimistically update UI
    setSwitchEnabled(checked);

    try {
      await talkingPalService.updateTalkingPal(selectedDevice.id, {
        switch: checked,
      });
    } catch (err) {
      // Revert on error
      setSwitchEnabled(!checked);
      showSnackbar(err instanceof Error ? err.message : 'Failed to update', 'error');
    }
  };

  // Conversation length is edited locally and saved on blur (no request per keystroke)
  const handleSessionLimitCommit = async () => {
    if (!selectedDevice) return;

    const parsed = Number(sessionLimitInput);
    if (
      !Number.isInteger(parsed) ||
      parsed < SESSION_LIMIT_MINUTES_MIN ||
      parsed > SESSION_LIMIT_MINUTES_MAX
    ) {
      setSessionLimitInput(String(sessionLimit));
      showSnackbar(
        t('talkingpal.sessionLimitInvalid', {
          min: SESSION_LIMIT_MINUTES_MIN,
          max: SESSION_LIMIT_MINUTES_MAX,
        }),
        'error'
      );
      return;
    }
    if (parsed === sessionLimit) return;

    setSavingSessionLimit(true);
    try {
      const data = await talkingPalService.updateTalkingPal(selectedDevice.id, {
        session_limit_minutes: parsed,
      });
      setSessionLimit(data.session_limit_minutes);
      setSessionLimitInput(String(data.session_limit_minutes));
      showSnackbar(t('talkingpal.saved'), 'success');
    } catch (err) {
      setSessionLimitInput(String(sessionLimit));
      showSnackbar(err instanceof Error ? err.message : 'Failed to save', 'error');
    } finally {
      setSavingSessionLimit(false);
    }
  };

  // Send additional prompt
  const handleSendPrompt = async () => {
    if (!selectedDevice || !additionalPrompt.trim()) return;

    setSendingPrompt(true);
    try {
      const data = await talkingPalService.sendPrompt(selectedDevice.id, additionalPrompt.trim());
      setPromptHistory(data.prompt_history || []);
      setCurrentPrompt(data.current_prompt);
      setAdditionalPrompt('');
      showSnackbar(t('talkingpal.promptSent'), 'success');
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to send prompt', 'error');
    } finally {
      setSendingPrompt(false);
    }
  };

  // Clear current prompt
  const handleClearPrompt = async () => {
    if (!selectedDevice) return;

    try {
      const data = await talkingPalService.clearPrompt(selectedDevice.id);
      setCurrentPrompt(data.current_prompt);
      showSnackbar(t('talkingpal.promptCleared'), 'success');
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to clear prompt', 'error');
    }
  };

  // Load prompt from history into textbox
  const handleHistoryClick = (prompt: string) => {
    setAdditionalPrompt(prompt);
  };

  // Handle key press for sending prompt
  const handlePromptKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendPrompt();
    }
  };

  // Save nursery rhyme (add or update)
  const handleSaveRhyme = async () => {
    if (!selectedDevice || !rhymeText.trim()) return;

    setSavingRhyme(true);
    try {
      const data = await talkingPalService.saveNurseryRhyme(
        selectedDevice.id,
        rhymeText.trim(),
        editingRhymeIndex !== null ? editingRhymeIndex : undefined
      );
      setNurseryRhymes(data.nursery_rhymes || []);
      setRhymeText('');
      setEditingRhymeIndex(null);
      showSnackbar(
        editingRhymeIndex !== null ? t('talkingpal.rhymeUpdated') : t('talkingpal.rhymeSaved'),
        'success'
      );
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to save rhyme', 'error');
    } finally {
      setSavingRhyme(false);
    }
  };

  // Edit a rhyme (load into textbox)
  const handleEditRhyme = (index: number) => {
    setRhymeText(nurseryRhymes[index]);
    setEditingRhymeIndex(index);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setRhymeText('');
    setEditingRhymeIndex(null);
  };

  // Delete a rhyme
  const handleDeleteRhyme = async (index: number) => {
    if (!selectedDevice) return;

    try {
      const data = await talkingPalService.deleteNurseryRhyme(selectedDevice.id, index);
      setNurseryRhymes(data.nursery_rhymes || []);
      // If we were editing this rhyme, clear the form
      if (editingRhymeIndex === index) {
        setRhymeText('');
        setEditingRhymeIndex(null);
      } else if (editingRhymeIndex !== null && editingRhymeIndex > index) {
        // Adjust editing index if a rhyme before it was deleted
        setEditingRhymeIndex(editingRhymeIndex - 1);
      }
      showSnackbar(t('talkingpal.rhymeDeleted'), 'success');
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to delete rhyme', 'error');
    }
  };

  if (!selectedDevice) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      {/* System Prompt and Switch Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('talkingpal.title')}</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={switchEnabled}
                  onChange={(e) => handleSwitchToggle(e.target.checked)}
                  color="primary"
                />
              }
              label={t('talkingpal.switch')}
            />
          </Box>

          <TextField
            type="number"
            label={t('talkingpal.sessionLimit')}
            value={sessionLimitInput}
            onChange={(e) => setSessionLimitInput(e.target.value)}
            onBlur={handleSessionLimitCommit}
            disabled={savingSessionLimit}
            inputProps={{ min: SESSION_LIMIT_MINUTES_MIN, max: SESSION_LIMIT_MINUTES_MAX, step: 1 }}
            helperText={t('talkingpal.sessionLimitHelp', {
              min: SESSION_LIMIT_MINUTES_MIN,
              max: SESSION_LIMIT_MINUTES_MAX,
            })}
            sx={{ mb: 3, maxWidth: 360 }}
            fullWidth
          />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('talkingpal.systemPrompt')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder={t('talkingpal.systemPromptPlaceholder')}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
          >
            {saving ? <CircularProgress size={24} /> : t('common.save')}
          </Button>
        </CardContent>
      </Card>

      {/* Additional Prompts Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('talkingpal.additionalPrompts')}
          </Typography>

          {/* Current Active Prompt */}
          {currentPrompt && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <IconButton
                  aria-label="clear"
                  color="inherit"
                  size="small"
                  onClick={handleClearPrompt}
                >
                  <Clear fontSize="inherit" />
                </IconButton>
              }
            >
              <Typography variant="body2">
                <strong>{t('talkingpal.currentPrompt')}:</strong> {currentPrompt}
              </Typography>
            </Alert>
          )}

          {/* Send Prompt Input */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              value={additionalPrompt}
              onChange={(e) => setAdditionalPrompt(e.target.value)}
              onKeyPress={handlePromptKeyPress}
              placeholder={t('talkingpal.promptPlaceholder')}
              variant="outlined"
              size="small"
              disabled={sendingPrompt}
            />
            <Button
              variant="contained"
              onClick={handleSendPrompt}
              disabled={sendingPrompt || !additionalPrompt.trim()}
              startIcon={sendingPrompt ? <CircularProgress size={20} /> : <Send />}
            >
              {t('talkingpal.sendPrompt')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearPrompt}
              disabled={!currentPrompt}
              startIcon={<Clear />}
            >
              {t('talkingpal.clearPrompt')}
            </Button>
          </Box>

          {/* Prompt History */}
          {promptHistory.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('talkingpal.promptHistory')}
              </Typography>
              <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                {promptHistory.map((prompt, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider />}
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleHistoryClick(prompt)}>
                        <ListItemText
                          primary={prompt}
                          primaryTypographyProps={{
                            noWrap: true,
                            sx: { maxWidth: '100%' },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </CardContent>
      </Card>

      {/* Nursery Rhymes Section */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('talkingpal.nurseryRhymes')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('talkingpal.nurseryRhymesDescription')}
          </Typography>

          {/* Rhyme Input */}
          <TextField
            fullWidth
            multiline
            rows={4}
            value={rhymeText}
            onChange={(e) => setRhymeText(e.target.value)}
            placeholder={t('talkingpal.rhymePlaceholder')}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveRhyme}
              disabled={savingRhyme || !rhymeText.trim()}
            >
              {savingRhyme ? (
                <CircularProgress size={24} />
              ) : editingRhymeIndex !== null ? (
                t('talkingpal.updateRhyme')
              ) : (
                t('talkingpal.saveRhyme')
              )}
            </Button>
            {editingRhymeIndex !== null && (
              <Button variant="outlined" onClick={handleCancelEdit}>
                {t('common.cancel')}
              </Button>
            )}
          </Box>

          {/* Saved Rhymes List */}
          {nurseryRhymes.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('talkingpal.savedRhymes')} ({nurseryRhymes.length})
              </Typography>
              <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                {nurseryRhymes.map((rhyme, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider />}
                    <ListItem
                      secondaryAction={
                        <Box>
                          <IconButton
                            edge="end"
                            aria-label="edit"
                            onClick={() => handleEditRhyme(index)}
                            sx={{ mr: 1 }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeleteRhyme(index)}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      }
                      sx={{
                        bgcolor: editingRhymeIndex === index ? 'action.selected' : 'transparent',
                      }}
                    >
                      <ListItemButton onClick={() => handleEditRhyme(index)}>
                        <ListItemText
                          primary={rhyme.length > 100 ? `${rhyme.substring(0, 100)}...` : rhyme}
                          primaryTypographyProps={{
                            sx: { whiteSpace: 'pre-wrap' },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default TalkingpalPage;
